import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDateRangeStore } from '@/stores/dateRangeStore';
import { salesApi } from '@/services/api';
import { DateRangeSelector } from '@/components/dashboard/DateRangeSelector';
import { AgentCard } from '@/components/dashboard/AgentCard';
import { RecentSales } from '@/components/dashboard/RecentSales';
import type { SalesSummary } from '@/types';

export function Agents() {
  const { t } = useTranslation();
  const { preset, startDate, endDate } = useDateRangeStore();

  const { data: summary, isLoading } = useQuery<SalesSummary>({
    queryKey: ['sales-summary', preset, startDate.toISOString(), endDate.toISOString()],
    queryFn: () =>
      salesApi.getSummary({
        preset,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      }),
    refetchInterval: 60000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('agents.title')}</h1>
        <DateRangeSelector />
      </div>

      {/* Agent Cards */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {t('agents.performance')}
        </h2>
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : summary?.agentSummaries?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {summary.agentSummaries.map((agent) => (
              <AgentCard key={agent.agentId} agent={{ ...agent, name: agent.agentName }} />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500">
            {t('dashboard.noData')}
          </div>
        )}
      </div>

      {/* Recent Sales */}
      <RecentSales limit={15} />
    </div>
  );
}
